
import { useEffect, useRef, useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Lead } from '@/types/lead';
import { getWebSearchData, getWebSearchStatus, enrichLeadWithWebSearch } from '@/services/webSearchService';
import { supabase } from '@/integrations/supabase/client';
import { WebSearchData, WebSearchStatus } from '@/types/webSearch';

interface WebSearchJobState {
  jobId: string | null;
  status: string | null;
  currentPage: string | null;
  errorMessage: string | null;
}

export const useWebSearch = (lead: Lead, onDataUpdated?: () => void, showLinkedInSection: boolean = false) => {
  const { toast } = useToast();
  const autoFixRanRef = useRef(false);

  const [searchData, setSearchData] = useState<WebSearchData | null>(null);
  const [searchStatus, setSearchStatus] = useState<WebSearchStatus | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [queuedAt, setQueuedAt] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [progressValue, setProgressValue] = useState(0);
  const [queuePosition, setQueuePosition] = useState<number | null>(null);
  const [jobState, setJobState] = useState<WebSearchJobState>({ jobId: null, status: null, currentPage: null, errorMessage: null });

  const calculateProgress = (status: WebSearchStatus | null) => {
    switch (status) {
      case 'pending': return 15;
      case 'processing': return 60;
      case 'completed': return 100;
      case 'failed': return 100;
      default: return 0;
    }
  };

  const refreshSearchData = useCallback(async () => {
    const data = await getWebSearchData(lead.id);
    setSearchData(data);
  }, [lead.id]);

  // Realtime subscription to enrichment_jobs for web_search
  useEffect(() => {
    const channel = supabase
      .channel(`enrichment-job-${lead.id}-ws`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'enrichment_jobs',
        filter: `entity_id=eq.${lead.id}`,
      }, (payload) => {
        const job = payload.new as any;
        if (!job || job.job_type !== 'web_search') return;

        console.log(`[useWebSearch] Job update: ${job.status} — ${job.current_page}`);
        
        setJobState({
          jobId: job.id,
          status: job.status,
          currentPage: job.current_page,
          errorMessage: job.error_message,
        });

        if (job.status === 'processing') {
          setSearchStatus('processing');
          setProgressValue(60);
        } else if (job.status === 'completed') {
          // Only show completion if we're past step transitions
          const page = job.current_page;
          if (page !== 'step1_complete') {
            setSearchStatus('completed');
            setProgressValue(100);
            toast({ title: "Web search completed", description: `Information retrieved for ${lead.name}` });
          }
          // Clear jobState so the pipeline banner dismisses
          setJobState({ jobId: null, status: null, currentPage: null, errorMessage: null });
          refreshSearchData();
          onDataUpdated?.();
        } else if (job.status === 'failed') {
          setSearchStatus('failed');
          setProgressValue(100);
          toast({ variant: "destructive", title: "Web search failed", description: job.error_message || "An error occurred during enrichment." });
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [lead.id, lead.name, toast, onDataUpdated, refreshSearchData]);

  // Also subscribe to lead-level changes for status sync
  useEffect(() => {
    const channel = supabase
      .channel(`lead-ws-status-${lead.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'leads',
        filter: `id=eq.${lead.id}`,
      }, (payload) => {
        const updated = payload.new as any;
        const newStatus = updated.web_search_status as WebSearchStatus;
        if (newStatus && newStatus !== searchStatus) {
          console.log(`Lead updated via realtime:`, { id: lead.id, web_search_status: newStatus, crawl_status: updated.crawl_status });
          setSearchStatus(newStatus);
          setProgressValue(calculateProgress(newStatus));
          
          if (newStatus === 'completed' && !searchData) {
            refreshSearchData();
          }
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [lead.id, searchStatus, searchData, refreshSearchData]);

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      setSearchStatus('pending');
      setProgressValue(calculateProgress('pending'));
      
      const jobId = await enrichLeadWithWebSearch(lead);
      
      if (jobId) {
        setJobState(prev => ({ ...prev, jobId, status: 'pending' }));
        toast({ title: "Web search started", description: `Retrieving web information for ${lead.name}` });
      } else {
        setSearchStatus('failed');
        setProgressValue(calculateProgress('failed'));
        toast({ variant: "destructive", title: "Web search failed", description: "Failed to start enrichment." });
      }
    } catch (error) {
      console.error('Error refreshing web search data:', error);
      setSearchStatus('failed');
      setProgressValue(calculateProgress('failed'));
      toast({ variant: "destructive", title: "Web search failed", description: "An error occurred while starting web information retrieval." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleProcessQueue = async () => {
    // Queue processing now just triggers enrichment for this lead
    setIsProcessing(true);
    try {
      const jobId = await enrichLeadWithWebSearch(lead);
      if (jobId) {
        toast({ title: "Processing started", description: "Enrichment job created." });
      }
    } catch (error) {
      console.error('Error processing queue:', error);
      toast({ variant: "destructive", title: "Failed to process", description: "An error occurred." });
    } finally {
      setIsProcessing(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      setIsInitialLoading(true);
      try {
        const data = await getWebSearchData(lead.id);
        setSearchData(data);

        const status = await getWebSearchStatus(lead.id);
        const liSource = data?.linkedIn?.dataSource;
        
        if (liSource === 'verified_linkedin') {
          setSearchStatus('completed');
          setProgressValue(calculateProgress('completed'));
        } else {
          setSearchStatus(status?.status || null);
          setProgressValue(calculateProgress(status?.status || null));
        }

        setLastUpdated(status?.updatedAt || null);
        setQueuedAt(status?.queuedAt || null);

        if (status?.status === 'pending') {
          setQueuePosition(status.queuePosition);
        }

        // Auto-cleanup stale web_search jobs (older than 15 min)
        const fifteenMinAgo = new Date(Date.now() - 15 * 60 * 1000).toISOString();
        await supabase
          .from('enrichment_jobs')
          .update({ status: 'failed', error_message: 'Auto-cleaned: stale job' })
          .eq('entity_id', lead.id)
          .eq('job_type', 'web_search')
          .in('status', ['pending', 'processing'])
          .lt('updated_at', fifteenMinAgo);

        // Only check for active jobs if lead isn't already completed
        if (status?.status !== 'completed') {
          const { data: activeJobs } = await supabase
            .from('enrichment_jobs')
            .select('id, status, current_page, error_message')
            .eq('entity_id', lead.id)
            .eq('job_type', 'web_search')
            .in('status', ['pending', 'processing'])
            .gte('updated_at', fifteenMinAgo)
            .order('created_at', { ascending: false })
            .limit(1);

          if (activeJobs?.[0]) {
            setJobState({
              jobId: activeJobs[0].id,
              status: activeJobs[0].status,
              currentPage: activeJobs[0].current_page,
              errorMessage: activeJobs[0].error_message,
            });
            setSearchStatus(activeJobs[0].status === 'processing' ? 'processing' : 'pending');
            setProgressValue(calculateProgress(activeJobs[0].status === 'processing' ? 'processing' : 'pending'));
          }
        }
      } catch (error) {
        console.error('Error fetching web search data:', error);
      } finally {
        setIsInitialLoading(false);
      }
    };

    fetchData();
  }, [lead.id]);

  return {
    searchData,
    searchStatus,
    lastUpdated,
    queuedAt,
    isLoading,
    isProcessing,
    isInitialLoading,
    progressValue,
    queuePosition,
    handleRefresh,
    handleProcessQueue,
    refreshSearchData,
    jobState,
  };
};
